import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div className="border-b border-white/10 last:border-none">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex justify-between items-center text-left"
      >
        <h3 className="text-xl font-semibold text-white/95">{question}</h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDownIcon className="w-6 h-6 text-white/70" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-4 text-white/80 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const InfoSection: React.FC = () => {
  const faqs = [
    {
      question: "What is pomodoro timer technique?",
      answer: "The Pomodoro Technique is a time management method that uses a timer to break work into 25-minute focused sessions, separated by short breaks. This technique helps improve productivity and maintain mental freshness."
    },
    {
      question: "How do I use this online pomodoro timer?",
      answer: "Simply click the play button to start your 25-minute focus session. When the timer ends, take a short 5-minute break. After completing four pomodoros, take a longer 15-minute break. You can customize these durations in the settings."
    },
    {
      question: "Can I use this pomodoro timer on my phone?",
      answer: "Yes! Our pomodoro timer online is fully responsive and works perfectly on mobile devices, tablets, and computers, whether you're using Mac, Windows, or any other platform."
    },
    {
      question: "Why should I use a pomodoro timer for studying?",
      answer: "A study timer pomodoro helps maintain focus and prevent mental fatigue. By breaking study sessions into manageable chunks, you can improve retention and maintain high levels of concentration throughout your study session."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="mt-12 text-white/90 space-y-12 max-w-4xl mx-auto px-4 sm:px-6 pb-12"
    >
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center">What is a Pomodoro Timer?</h2>
        <p className="leading-relaxed text-lg text-center max-w-3xl mx-auto">
          A Pomodoro Timer is a powerful productivity tool based on the Pomodoro Technique, developed by Francesco Cirillo. 
          This aesthetic pomodoro timer helps you break your work into focused 25-minute intervals, followed by short breaks, 
          making it the perfect online study timer for students and professionals alike.
        </p>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center">Why Choose Our Online Pomodoro Timer?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {[
            {
              icon: "✨",
              title: "Beautiful Design",
              description: "Experience productivity with our aesthetic pomodoro timer, featuring a clean, modern interface that works perfectly on both pomodoro timer Mac and Windows systems."
            },
            {
              icon: "🎯",
              title: "Stay Focused",
              description: "Our online pomodoro timer helps you maintain focus with customizable work sessions, perfect for any study timer pomodoro session."
            },
            {
              icon: "📊",
              title: "Track Progress",
              description: "Monitor your productivity with built-in statistics, helping you understand and improve your work patterns."
            },
            {
              icon: "🔔",
              title: "Smart Notifications",
              description: "Never miss a break with our intelligent notification system, available on all devices."
            }
          ].map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="bg-white/10 backdrop-blur-lg rounded-xl p-6 hover:bg-white/20 transition-colors"
            >
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold text-white/95 mb-2">{feature.title}</h3>
              <p className="text-white/80">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
          {faqs.map((faq) => (
            <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center">Tips for Using Our Pomodoro Timer</h2>
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
          <ul className="space-y-4">
            {[
              "Start with one task at a time",
              "Use the task list feature to track your progress",
              "Take breaks seriously - they're essential for productivity",
              "Customize timer durations to match your work style",
              "Enable notifications to stay on track"
            ].map((tip, index) => (
              <motion.li
                key={tip}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex items-center space-x-3"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  {index + 1}
                </div>
                <span>{tip}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </motion.section>

      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-center text-white/70 pt-8"
      >
        <p className="text-lg">Start boosting your productivity today with our aesthetic pomodoro timer online!</p>
      </motion.footer>
    </motion.div>
  );
};

export default InfoSection;
